import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Admin.css';

interface Shop {
  id: number;
  name: string;
  address: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string;
}

interface User {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  shop_id: number;
  role: string;  // 'admin', 'manager', 'cashier'
  is_active: boolean;
  created_at: string;
}

interface ShopStats {
  shop_id: number;
  shop_name: string;
  users_count: number;
  products_count: number;
  sales_count: number;
  total_revenue: number;
}

interface AdminStats {
  total_shops: number;
  total_users: number;
  active_users: number;
  inactive_users: number;
  total_products: number;
  total_sales: number;
  total_revenue: number;
  top_shops: Array<{
    shop_id: number;
    shop_name: string;
    revenue: number;
  }>;
}

const Admin: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'shops' | 'users'>('overview');
  
  // États pour les statistiques
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  
  // États pour les boutiques
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [shopStats, setShopStats] = useState<ShopStats | null>(null);
  const [showShopModal, setShowShopModal] = useState(false);
  const [shopFormData, setShopFormData] = useState({
    name: '',
    address: '',
    phone: ''
  });
  
  // États pour les utilisateurs
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [filterShopId, setFilterShopId] = useState<number | null>(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    shop_id: 0,
    role: 'cashier' as 'admin' | 'manager' | 'cashier'
  });

  // Handling password change requirement
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'overview') {
      loadAdminStats();
    } else if (activeTab === 'shops') {
      loadShops();
    } else if (activeTab === 'users') {
      loadUsers();
      loadShops(); // Pour le dropdown de sélection
    }
  }, [activeTab]);

  // ==================== CHARGEMENT DES DONNÉES ====================

  const loadAdminStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/stats/overview', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminStats(response.data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des statistiques:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const loadShops = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/shops', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShops(response.data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des boutiques:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des boutiques');
    } finally {
      setLoading(false);
    }
  };

  const loadShopStats = async (shopId: number) => {
    try {
      const response = await api.get(`/admin/shops/${shopId}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShopStats(response.data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des stats boutique:', err);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filterShopId ? { shop_id: filterShopId } : {};
      const response = await api.get('/admin/users', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des utilisateurs:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  // ==================== GESTION DES BOUTIQUES ====================

  const handleCreateShop = () => {
    setSelectedShop(null);
    setShopFormData({ name: '', address: '', phone: '' });
    setShowShopModal(true);
  };

  const handleEditShop = (shop: Shop) => {
    setSelectedShop(shop);
    setShopFormData({
      name: shop.name,
      address: shop.address || '',
      phone: shop.phone || ''
    });
    setShowShopModal(true);
  };

  const handleViewShopStats = async (shop: Shop) => {
    setSelectedShop(shop);
    await loadShopStats(shop.id);
  };

  const handleSaveShop = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (selectedShop) {
        // Mise à jour
        await api.put(`/admin/shops/${selectedShop.id}`, shopFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Boutique mise à jour avec succès');
      } else {
        // Création
        await api.post('/admin/shops', shopFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Boutique créée avec succès');
      }
      
      setShowShopModal(false);
      loadShops();
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde de la boutique:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShop = async (shopId: number, shopName: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la boutique "${shopName}" ? Cette action est irréversible.`)) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/admin/shops/${shopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Boutique supprimée avec succès');
      loadShops();
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  // ==================== GESTION DES UTILISATEURS ====================

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      shop_id: shops.length > 0 ? shops[0].id : 0,
      role: 'cashier'
    });

    setPasswordError(null);
    setIsPasswordValid(false);
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserFormData({
      name: user.name,
      email: user.email || '',
      phone: user.phone || '',
      password: '', // Ne pas pré-remplir le mot de passe
      confirmPassword: '',
      shop_id: user.shop_id,
      role: user.role as 'admin' | 'manager' | 'cashier'
    });
    setPasswordError(null);
    setIsPasswordValid(true); // Valide par défaut en mode édition sans changement de mot de passe
    setShowUserModal(true);
  };

  const handleConfirmPassword = (confirmPassword: string | null) => {
    setUserFormData({ ...userFormData, confirmPassword: confirmPassword || '' });

    // Si on est en mode édition et que le mot de passe est vide, pas de validation nécessaire
    if(selectedUser && !userFormData.password && !confirmPassword) {
      setIsPasswordValid(true);
      setPasswordError(null);
      return;
    }

    // Vérifier si un mot de passe est fourni
    if(!userFormData.password && !selectedUser){
      setIsPasswordValid(false);
      setPasswordError('Le mot de passe est requis');
      return;
    }

    // Vérifier la longueur minimale
    if (userFormData.password && userFormData.password.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
      setIsPasswordValid(false);
      return;
    } 

    // Vérifier la correspondance
    if (userFormData.password !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      setIsPasswordValid(false);
      return;
    }

    // Tout est OK
    setPasswordError(null);
    setIsPasswordValid(true);
  }

  const handlePasswordChange = (password: string | null) => {
    setUserFormData({ ...userFormData, password: password || '' });
    
    // Révalider si un mot de passe de confirmation existe déjà
    if (userFormData.confirmPassword) {
      handleConfirmPassword(userFormData.confirmPassword);
    } else {
      // Vérifier seulement la longueur
      if (password && password.length < 6) {
        setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
        setIsPasswordValid(false);
      } else {
        setPasswordError(null);
        // Ne pas valider tant que la confirmation n'est pas saisie
        setIsPasswordValid(false);
      }
    }
  }

  const isFormValid = () => {
    // Validation de base
    if (!userFormData.name || !userFormData.shop_id) {
      return false;
    }

    // Si on crée un nouvel utilisateur, le mot de passe est obligatoire
    if (!selectedUser && !userFormData.password) {
      return false;
    }

    // Si un mot de passe est saisi (création ou modification), il doit être valide
    if (userFormData.password || userFormData.confirmPassword) {
      return isPasswordValid;
    }

    // Si on modifie sans changer le mot de passe
    return true;
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setError('Veuillez corriger les erreurs avant de soumettre');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Add +221 prefix only if phone is provided and doesn't already have it
      if (userFormData.phone) {
        userFormData.phone = userFormData.phone.replace(/^\+221/, ''); // Remove if already present
        userFormData.phone = '+221' + userFormData.phone;
      }

      if (selectedUser) {
        // Mise à jour - ne pas envoyer le mot de passe s'il est vide
        const updateData: any = {
          name: userFormData.name,
          email: userFormData.email || null,
          phone: userFormData.phone || null,
          role: userFormData.role,
        };
        
        // N'envoyer le mot de passe que s'il est fourni
        if (userFormData.password && userFormData.password.trim() !== '') {
          updateData.password = userFormData.password;
        }
        
        await api.put(
          `/admin/users/${selectedUser.id}?shop_id=${userFormData.shop_id}`,
          updateData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        alert('Utilisateur mis à jour avec succès');
      } else {
        // Création
        await api.post(
          `/admin/users?shop_id=${userFormData.shop_id}`,
          {
            name: userFormData.name,
            email: userFormData.email || null,
            phone: userFormData.phone || null,
            password: userFormData.password,
            role: userFormData.role,
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        alert('Utilisateur créé avec succès');
      }
      
      setShowUserModal(false);
      loadUsers();
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde de l\'utilisateur:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserActive = async (userId: number, userName: string, isActive: boolean) => {
    const action = isActive ? 'désactiver' : 'activer';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} l'utilisateur "${userName}" ?`)) {
      return;
    }

    setLoading(true);
    try {
      await api.put(`/admin/users/${userId}/toggle-active`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Utilisateur ${action === 'activer' ? 'activé' : 'désactivé'} avec succès`);
      loadUsers();
    } catch (err: any) {
      console.error('Erreur lors du changement de statut:', err);
      alert(err.response?.data?.detail || 'Erreur lors du changement de statut');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userName}" ? Cette action est irréversible.`)) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Utilisateur supprimé avec succès');
      loadUsers();
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const getShopName = (shopId: number) => {
    const shop = shops.find(s => s.id === shopId);
    return shop ? shop.name : `Boutique #${shopId}`;
  };

  const getRoleLabel = (role: string) => {
    const roleLabels: { [key: string]: string } = {
      admin: '👑 Administrateur',
      manager: '⭐ Gérant',
      cashier: '💼 Caissier'
    };
    return roleLabels[role] || role;
  };

  const getRoleBadgeClass = (role: string) => {
    const roleClasses: { [key: string]: string } = {
      admin: 'role-admin',
      manager: 'role-manager',
      cashier: 'role-cashier'
    };
    return roleClasses[role] || 'role-cashier';
  };

  // ==================== RENDU ====================

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>🔧 Administration</h1>
        <p>Gestion des boutiques et utilisateurs</p>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Navigation par onglets */}
      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Vue d'ensemble
        </button>
        <button
          className={`tab-button ${activeTab === 'shops' ? 'active' : ''}`}
          onClick={() => setActiveTab('shops')}
        >
          🏪 Boutiques
        </button>
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Utilisateurs
        </button>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && adminStats && (
        <div className="overview-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🏪</div>
              <div className="stat-content">
                <div className="stat-value">{adminStats.total_shops}</div>
                <div className="stat-label">Boutiques</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-value">{adminStats.total_users}</div>
                <div className="stat-label">Utilisateurs</div>
                <div className="stat-detail">
                  {adminStats.active_users} actifs, {adminStats.inactive_users} inactifs
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📦</div>
              <div className="stat-content">
                <div className="stat-value">{adminStats.total_products}</div>
                <div className="stat-label">Produits</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🛒</div>
              <div className="stat-content">
                <div className="stat-value">{adminStats.total_sales}</div>
                <div className="stat-label">Ventes</div>
              </div>
            </div>

            <div className="stat-card highlight">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <div className="stat-value">{adminStats.total_revenue.toLocaleString()} FCFA</div>
                <div className="stat-label">Chiffre d'affaires total</div>
              </div>
            </div>
          </div>

          <div className="top-shops-section">
            <h2>🏆 Top 5 Boutiques</h2>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Boutique</th>
                  <th>Chiffre d'affaires</th>
                </tr>
              </thead>
              <tbody>
                {adminStats.top_shops.map((shop, index) => (
                  <tr key={shop.shop_id}>
                    <td>{index + 1}</td>
                    <td>{shop.shop_name}</td>
                    <td className="revenue">{shop.revenue.toLocaleString()} FCFA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SHOPS TAB */}
      {activeTab === 'shops' && (
        <div className="shops-section">
          <div className="section-header">
            <h2>Gestion des Boutiques</h2>
            <button className="btn-primary" onClick={handleCreateShop}>
              ➕ Nouvelle Boutique
            </button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Adresse</th>
                  <th>Téléphone</th>
                  <th>Date création</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shops.map(shop => (
                  <tr key={shop.id}>
                    <td>{shop.id}</td>
                    <td><strong>{shop.name}</strong></td>
                    <td>{shop.address || '—'}</td>
                    <td>{shop.phone || '—'}</td>
                    <td>{new Date(shop.created_at).toLocaleDateString()}</td>
                    <td className="actions">
                      <button
                        className="btn-icon"
                        title="Voir statistiques"
                        onClick={() => handleViewShopStats(shop)}
                      >
                        📊
                      </button>
                      <button
                        className="btn-icon"
                        title="Modifier"
                        onClick={() => handleEditShop(shop)}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Supprimer"
                        onClick={() => handleDeleteShop(shop.id, shop.name)}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Stats boutique sélectionnée */}
          {shopStats && selectedShop && (
            <div className="shop-stats-modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>📊 Statistiques - {shopStats.shop_name}</h3>
                  <button onClick={() => setShopStats(null)}>✕</button>
                </div>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-label">Utilisateurs</div>
                    <div className="stat-value">{shopStats.users_count}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Produits</div>
                    <div className="stat-value">{shopStats.products_count}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Ventes</div>
                    <div className="stat-value">{shopStats.sales_count}</div>
                  </div>
                  <div className="stat-card highlight">
                    <div className="stat-label">CA Total</div>
                    <div className="stat-value">{shopStats.total_revenue.toLocaleString()} FCFA</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* USERS TAB */}
      {activeTab === 'users' && (
        <div className="users-section">
          <div className="section-header">
            <h2>Gestion des Utilisateurs</h2>
            <div className="header-actions">
              <select
                value={filterShopId || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  setFilterShopId(value);
                }}
                className="filter-select"
              >
                <option value="">Toutes les boutiques</option>
                {shops.map(shop => (
                  <option key={shop.id} value={shop.id}>{shop.name}</option>
                ))}
              </select>
              <button className="btn-primary" onClick={handleCreateUser}>
                ➕ Nouvel Utilisateur
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Boutique</th>
                  <th>Rôle</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={!user.is_active ? 'inactive-user' : ''}>
                    <td>{user.id}</td>
                    <td><strong>{user.name}</strong></td>
                    <td>{user.email || '—'}</td>
                    <td>{user.phone || '—'}</td>
                    <td>{getShopName(user.shop_id)}</td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? '✓ Actif' : '✗ Inactif'}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-icon"
                        title="Modifier"
                        onClick={() => handleEditUser(user)}
                      >
                        ✏️
                      </button>
                      <button
                        className={`btn-icon ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                        title={user.is_active ? 'Désactiver' : 'Activer'}
                        onClick={() => handleToggleUserActive(user.id, user.name, user.is_active)}
                      >
                        {user.is_active ? '🔒' : '🔓'}
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Supprimer"
                        onClick={() => handleDeleteUser(user.id, user.name)}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* MODAL BOUTIQUE */}
      {showShopModal && (
        <div className="modal-overlay" onClick={() => setShowShopModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedShop ? '✏️ Modifier la boutique' : '➕ Nouvelle boutique'}</h3>
              <button onClick={() => setShowShopModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSaveShop}>
              <div className="form-group">
                <label htmlFor="shop-name">Nom de la boutique *</label>
                <input
                  id="shop-name"
                  type="text"
                  value={shopFormData.name}
                  onChange={(e) => setShopFormData({ ...shopFormData, name: e.target.value })}
                  required
                  placeholder="Ex: Boutique Centre-Ville"
                />
              </div>
              <div className="form-group">
                <label htmlFor="shop-address">Adresse</label>
                <input
                  id="shop-address"
                  type="text"
                  value={shopFormData.address}
                  onChange={(e) => setShopFormData({ ...shopFormData, address: e.target.value })}
                  placeholder="Ex: 123 Rue Principale"
                />
              </div>
              <div className="form-group">
                <label htmlFor="shop-phone">Téléphone</label>
                <input
                  id="shop-phone"
                  type="tel"
                  value={shopFormData.phone}
                  onChange={(e) => setShopFormData({ ...shopFormData, phone: e.target.value })}
                  placeholder="Ex: +225 07 00 00 00 00"
                />
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowShopModal(false)} className="btn-secondary">
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Sauvegarde...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL UTILISATEUR */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedUser ? '✏️ Modifier l\'utilisateur' : '➕ Nouvel utilisateur'}</h3>
              <button onClick={() => setShowUserModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSaveUser}>
              <div className="form-group">
                <label htmlFor="user-name">Nom complet *</label>
                <input
                  id="user-name"
                  type="text"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                  required
                  placeholder="Ex: Jean Dupont"
                />
              </div>
              <div className="form-group">
                <label htmlFor="user-email">Email</label>
                <input
                  id="user-email"
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="Ex: jean.dupont@example.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="user-phone">Téléphone</label>
                <div className="input-group">
                  <span className="input-addon">+221</span>
                  <input
                    id="user-phone"
                    type="tel"
                    value={userFormData.phone}
                    onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                    placeholder="Ex: +225 07 00 00 00 00"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="user-shop">Boutique *</label>
                <select
                  id="user-shop"
                  value={userFormData.shop_id}
                  onChange={(e) => setUserFormData({ ...userFormData, shop_id: parseInt(e.target.value) })}
                  required
                >
                  {shops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="user-role">Rôle *</label>
                <select
                  id="user-role"
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value as 'admin' | 'manager' | 'cashier' })}
                  required
                >
                  <option value="cashier">💼 Caissier</option>
                  <option value="manager">⭐ Gérant</option>
                  <option value="admin">👑 Administrateur</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="user-password">
                  Mot de passe {selectedUser ? '(laisser vide pour ne pas changer)' : '*'}
                </label>
                <input
                  id="user-password"
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  required={!selectedUser}
                  placeholder="••••••••"
                  minLength={6}
                />
                <small className="form-hint">Minimum 6 caractères</small>
              </div>
              <div className="form-group">
                <label htmlFor="user-confirm-password">
                  Confirmer le mot de passe {selectedUser ? '' : '*'}
                </label>
                <input
                  id="user-confirm-password"
                  type="password"
                  value={userFormData.confirmPassword}
                  onChange={(e) => handleConfirmPassword(e.target.value)}
                  required={!selectedUser}
                  placeholder="••••••••"
                  minLength={6}
                />
                {passwordError && (
                  <small className="form-error">⚠️ {passwordError}</small>
                )}
                {!passwordError && userFormData.password && userFormData.confirmPassword && isPasswordValid && (
                  <small className="form-success">✓ Les mots de passe correspondent</small>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowUserModal(false)} className="btn-secondary">
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={loading || !isFormValid()}
                >
                  {loading ? 'Sauvegarde...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
